import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback, createContext, useContext } from "react";
import Select from "./Select.jsx";
import { computeWindow, reconcileWindow, warnIndexKeyFallback, windowsEqual } from "../lib/virtualWindow.js";

/**
 * StickyFolder — a composable, recursively-nestable sticky-header primitive.
 *
 * Each folder contributes its sticky header height to descendants so nested
 * rows stack cleanly inside the shared scroll container.
 */

const StickyOffsetContext = createContext(0);

// Horizontal-indent depth. Each open folder indents its body by 13px + a
// guide border. That's the right read for shallow trees, but the recursive
// scope sidebar interleaves TWO StickyFolders per scope level (the dimension
// folder + the selected entry's sub-content), so a deep cluster chain
// (root → … → leaf) marches off the right edge and labels truncate to
// nothing. Past MAX_INDENT_DEPTH we stop adding indent — the body renders
// flush so deep levels keep their full label width. The vertical sticky
// stack + active bar still convey hierarchy. Other nesters stay well under
// the cap (ContextPanel ≤ 3, StudioMenu 1), so this is a no-op for them.
const IndentDepthContext = createContext(0);
const MAX_INDENT_DEPTH = 4;

function findScrollRoot(element) {
  if (!element) return null;
  // Find the nearest OverlayScrollbars host or sidebar scroll container
  const host = element.closest("[data-overlayscrollbars-initialize]") || element.closest("[data-sidebar-scroll]");
  if (!host) return null;
  // OverlayScrollbars creates a viewport element that handles actual scrolling
  return host.querySelector("[data-overlayscrollbars-viewport]") || host.querySelector(".os-viewport") || host || null;
}

export default function StickyFolder({
  header,
  details = null,
  toolbar = null,
  open = false,
  sticky,
  active = false,
  padded = false,
  className = "",
  children = null,
}) {
  const shouldStick = sticky !== undefined ? (sticky && open) : open;
  const stickyRef = useRef(null);
  const parentOffset = useContext(StickyOffsetContext);
  const indentDepth = useContext(IndentDepthContext);
  const indented = indentDepth < MAX_INDENT_DEPTH;

  const [isStuck, setIsStuck] = useState(false);
  const [stickyHeight, setStickyHeight] = useState(0);

  // When collapsing while stuck, scroll so the collapsed item lands at the top
  // of the visible area (below ancestor sticky headers). Runs as layoutEffect
  // so the scroll happens before paint — no visual jump.
  const prevOpenRef = useRef(open);
  useLayoutEffect(() => {
    if (prevOpenRef.current && !open && isStuck) {
      const section = stickyRef.current?.closest("section");
      const scrollRoot = findScrollRoot(stickyRef.current);
      if (section && scrollRoot) {
        const sectionTop = section.getBoundingClientRect().top;
        const rootTop = scrollRoot.getBoundingClientRect().top;
        scrollRoot.scrollTop += sectionTop - rootTop - parentOffset;
      }
    }
    prevOpenRef.current = open;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setIsStuck(false);
      setStickyHeight(0);
    }
  }, [open]);

  const stickyTop = parentOffset;
  const childOffset = shouldStick ? parentOffset + stickyHeight : parentOffset;
  const zIndex = Math.max(1, 10 - Math.round(parentOffset / 20));

  // Stuck detection via scroll listener. A header is "stuck" when:
  // 1. The section's top has scrolled above the header's top (the header is
  //    being held in place by position:sticky, separated from its section)
  // 2. The section's bottom is still below the header (content remains)
  useEffect(() => {
    if (!open) return undefined;
    const stickyNode = stickyRef.current;
    const section = stickyNode?.closest("section");
    if (!stickyNode || !section) return undefined;
    const scrollRoot = findScrollRoot(stickyNode);
    if (!scrollRoot) return undefined;

    let prevStuck = false;
    const check = () => {
      const headerRect = stickyNode.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const sectionAboveHeader = sectionRect.top < headerRect.top - 1;
      const hasContentBelow = sectionRect.bottom > headerRect.bottom + 2;
      const stuck = sectionAboveHeader && hasContentBelow;
      if (stuck !== prevStuck) {
        prevStuck = stuck;
        setIsStuck(stuck);
      }
    };

    check();
    scrollRoot.addEventListener("scroll", check, { passive: true });
    return () => scrollRoot.removeEventListener("scroll", check);
  }, [open, stickyTop]);

  // Measure the sticky header height so descendants can offset beneath it.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const stickyNode = stickyRef.current;
    if (!stickyNode) return undefined;

    const measure = () => {
      const h = stickyNode.getBoundingClientRect().height;
      setStickyHeight((prev) => (prev === h ? prev : h));
    };

    measure();
    window.addEventListener("resize", measure);

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(stickyNode);

    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [open]);

  return (
    <section
      className={["flex flex-col", className].filter(Boolean).join(" ")}
    >
      {/* Sticky header — only the header row sticks */}
      <div
        ref={stickyRef}
        className={[
          "relative grid bg-[color:var(--bg)]",
          active ? "after:pointer-events-none after:absolute after:inset-0 after:bg-[color:var(--green-soft)] after:shadow-[inset_2px_0_0_var(--green)]" : "",
          padded ? "pt-1 pb-1" : "",
        ].join(" ")}
        style={shouldStick ? { position: "sticky", top: `${stickyTop}px`, zIndex, overflow: "hidden" } : undefined}
        data-sticky-header
      >
        {header}
      </div>

      {/* Secondary details stay in normal flow and scroll beneath the sticky header. */}
      {details ? (
        <div className={[
          "relative grid bg-[color:var(--bg)]",
          (active && !open) ? "after:pointer-events-none after:absolute after:inset-0 after:bg-[color:var(--green-soft)] after:shadow-[inset_2px_0_0_var(--green)]" : ""
        ].join(" ")}>
          {details}
        </div>
      ) : null}

      {/* Toolbar stays in normal flow and scrolls beneath the sticky header. */}
      {open && toolbar ? (
        <div className={[
          "relative grid pt-[8px] pb-[8px] pl-[12px] pr-[8px] bg-[color:var(--bg)]"
        ].join(" ")}>
          {toolbar}
        </div>
      ) : null}

      {/* Stuck shadow line */}
      {isStuck && shouldStick ? (
        <div
          className="pointer-events-none"
          style={{
            position: "sticky",
            top: `${stickyTop + stickyHeight}px`,
            zIndex: zIndex - 1,
            height: 0,
            boxShadow: "0 -1px 0 var(--bg), 0 1px 0 var(--line)",
          }}
        />
      ) : null}

      {/* Collapsible body */}
      {open ? (
        <StickyOffsetContext.Provider value={childOffset}>
          <IndentDepthContext.Provider value={indentDepth + 1}>
            <div
              className={[
                "relative z-0 grid gap-0 mb-[13px]",
                indented ? "ml-[13px] border-l border-[color:var(--line-soft)]" : "",
              ].join(" ")}
            >
              {children}
            </div>
          </IndentDepthContext.Provider>
        </StickyOffsetContext.Provider>
      ) : null}
    </section>
  );
}

export function FolderList({ children, className = "" }) {
  return (
    <div
      className={["grid gap-0", className]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

// Stable per-item key. Prefer the item's own `id`; fall back to array index
// only as a last resort (and warn in dev). The fallback is the failure mode
// this component used to ship: keying the height cache by index meant a
// reorder/filter mapped a cached height onto the wrong item → scroll jumps.
function defaultItemKey(item, i) {
  if (item?.id != null) return item.id;
  warnIndexKeyFallback("VirtualFolderList");
  return i;
}

/**
 * VirtualFolderList — lightweight scroll-based windowing for long lists
 * inside a shared scroll container. Renders only items within the visible
 * viewport ± overscan buffer. Uses spacer divs to maintain scroll height.
 *
 * Windowing math is the shared, unit-tested ../lib/virtualWindow.js (see
 * DataTable for the other consumer). Two behaviours that matter here:
 *   - Heights are cached by a *stable item key*, not array index, so the cache
 *     survives reordering/filtering without the historic scroll jumps.
 *   - Scroll uses reconcileWindow's hysteresis: while the visible band stays
 *     inside the rendered overscan buffer it returns the same window object,
 *     and the `setRange` updater bails (no re-render).
 *
 * Props:
 *   items       — array of data items
 *   renderItem(item, index) — returns JSX for one row
 *   getItemKey(item, index) — stable key per item (default: item.id ?? index)
 *   overscan    — pixel buffer above/below viewport (default 600)
 *   itemHeight  — estimated item height for initial layout (default 80)
 *   threshold   — minimum items before windowing kicks in (default 30)
 */
export function VirtualFolderList({
  items,
  renderItem,
  invalidateKey,
  getItemKey,
  overscan = 600,
  itemHeight = 80,
  threshold = 30,
  className = "",
}) {
  const containerRef = useRef(null);
  const heightsRef = useRef(new Map());
  const [range, setRange] = useState(() => ({ start: 0, end: items.length, topH: 0, bottomH: 0 }));
  const windowed = items.length > threshold;
  const computeRef = useRef(null);

  // invalidateKey signals a data mutation (sort/filter/refresh) that may keep
  // items.length constant — it's in the scroll-effect deps below so the window
  // is force-recomputed when the *contents* change, not just the count.
  // (We don't clear the height cache on invalidate: the ResizeObserver keeps
  // cached heights current, and clearing caused scroll jumps by replacing
  // correct heights with the default estimate.)

  // Per-item keys (stringified for stable Map keys + data attributes). Heights
  // are cached against these, never against array index.
  const keyOf = getItemKey || defaultItemKey;
  const keys = useMemo(
    () => items.map((item, i) => String(keyOf(item, i))),
    [items, keyOf],
  );

  const getHeight = useCallback(
    (i) => heightsRef.current.get(keys[i]) ?? itemHeight,
    [keys, itemHeight],
  );
  // Read the latest getHeight from the (length-keyed) scroll effect without
  // listing it as a dependency — otherwise an inline getItemKey would re-run
  // the effect every render and loop on setRange.
  const getHeightRef = useRef(getHeight);
  getHeightRef.current = getHeight;

  // ResizeObserver: auto-update height cache (keyed by item key) when rendered
  // items resize, then force a recompute so spacer heights stay accurate.
  const roRef = useRef(null);
  if (!roRef.current && typeof ResizeObserver !== "undefined") {
    roRef.current = new ResizeObserver((entries) => {
      let changed = false;
      for (const entry of entries) {
        const key = entry.target.dataset?.vkey;
        if (key == null) continue;
        const h = entry.target.getBoundingClientRect().height;
        if (h > 0 && Math.abs((heightsRef.current.get(key) ?? 0) - h) > 1) {
          heightsRef.current.set(key, h);
          changed = true;
        }
      }
      if (changed && computeRef.current) computeRef.current(true);
    });
  }

  useEffect(() => {
    return () => { if (roRef.current) roRef.current.disconnect(); };
  }, []);

  useEffect(() => {
    if (!windowed) return;
    const container = containerRef.current;
    if (!container) return;
    const scrollRoot = findScrollRoot(container);
    if (!scrollRoot) {
      // No scroll root found — fall back to showing all items (no virtualization)
      setRange({ start: 0, end: items.length, topH: 0, bottomH: 0 });
      return;
    }

    // Translate container geometry into the shared window model: intoRows =
    // -containerTop (how far the list has scrolled past the viewport top),
    // viewportH = scroll-root height, overscan stays in pixels.
    const metrics = () => {
      const rootRect = scrollRoot.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top - rootRect.top;
      return {
        scrollTop: -containerTop,
        baselineTop: 0,
        viewportH: rootRect.height,
        count: items.length,
        heightOf: (i) => getHeightRef.current(i),
        overscanPx: overscan,
      };
    };

    // force=true rebuilds the window (fresh spacers) for data/height changes;
    // force=false applies hysteresis — reconcileWindow returns the same object
    // while the visible band stays buffered. The windowsEqual guard keeps a
    // forced recompute loop-safe even when the caller passes a fresh `items`
    // array each render (e.g. ExplorerTree's `nodes.slice(...)`): an unchanged
    // window returns the previous object, so setRange bails.
    const compute = (force) => {
      const m = metrics();
      setRange((prev) => {
        const next = force ? computeWindow(m) : reconcileWindow(prev, m);
        return windowsEqual(prev, next) ? prev : next;
      });
    };

    computeRef.current = compute;
    compute(true);
    const onScroll = () => compute(false);
    scrollRoot.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      scrollRoot.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      computeRef.current = null;
    };
  }, [windowed, items.length, overscan, invalidateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const measureRef = useCallback((node) => {
    if (!node) return;
    const key = node.dataset.vkey;
    if (key == null) return;
    const h = node.getBoundingClientRect().height;
    if (h > 0 && heightsRef.current.get(key) !== h) {
      heightsRef.current.set(key, h);
    }
    if (roRef.current) roRef.current.observe(node);
  }, []);

  // Below threshold — render all, no spacers
  if (!windowed) {
    return (
      <div className={className}>
        {items.map((item, i) => (
          <div key={keys[i]}>{renderItem(item, i)}</div>
        ))}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      {range.topH > 0 && <div style={{ height: range.topH }} />}
      {items.slice(range.start, range.end).map((item, i) => {
        const index = range.start + i;
        return (
          <div key={keys[index]} ref={measureRef} data-vkey={keys[index]}>
            {renderItem(item, index)}
          </div>
        );
      })}
      {range.bottomH > 0 && <div style={{ height: range.bottomH }} />}
    </div>
  );
}

/**
 * LoadingSkeleton — pulsing placeholder rows that match FolderListRow layout.
 * Used inside DeferredContent while simulated fetches are in-flight.
 */
export function LoadingSkeleton({ rows = 5 }) {
  return (
    <div className="grid gap-0">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="grid [grid-template-columns:14px_minmax(0,1fr)_auto] items-start gap-x-2 pl-[6px] pr-[9px] py-[9px]"
        >
          <span className="mt-[4px] h-[10px] w-[10px] rounded-full bg-[color:var(--line-soft)]" />
          <div className="grid gap-[5px]">
            <div
              className="h-[12px] rounded bg-[color:var(--line-soft)]"
              style={{ width: `${55 + ((i * 17) % 30)}%` }}
            />
            <div
              className="h-[11px] rounded bg-[color:var(--line-soft)]"
              style={{ width: `${70 + ((i * 23) % 25)}%`, opacity: 0.5 }}
            />
          </div>
          <div className="h-[12px] w-[28px] rounded bg-[color:var(--line-soft)]" />
        </div>
      ))}
    </div>
  );
}

/**
 * DeferredContent — wraps children with a simulated loading delay.
 * Shows LoadingSkeleton on mount and whenever `trigger` changes,
 * then reveals children after 375–625ms (mean ~500ms).
 */
export function DeferredContent({ trigger, rows = 5, children }) {
  const [ready, setReady] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setReady(false);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(
      () => setReady(true),
      375 + Math.random() * 250,
    );
    return () => clearTimeout(timeoutRef.current);
  }, [trigger]);

  return ready ? children : <LoadingSkeleton rows={rows} />;
}

export function FolderToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  sortValue,
  onSortChange,
  sorts,
  inputRef = null,
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_104px] gap-2 max-[900px]:grid-cols-1">
      <label className="grid gap-1">
        <span className="text-[11px] leading-[1.22] text-[color:var(--muted-soft)]">Find</span>
        <input
          ref={inputRef}
          type="search"
          className="min-h-[26px] w-full border border-[color:var(--line)] bg-[color:var(--control-bg)] px-2 text-[12px] text-[color:var(--ink)]"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </label>
      <label className="grid gap-1">
        <span className="text-[11px] leading-[1.22] text-[color:var(--muted-soft)]">Sort</span>
        <Select
          value={sortValue}
          onChange={onSortChange}
          options={sorts.map(([value, label]) => ({ value, label }))}
          ariaLabel="Sort"
          align="right"
          triggerStyle={{ minHeight: 26, height: 26, paddingTop: 0, paddingBottom: 0 }}
          className="bg-[color:var(--control-bg)] px-2"
        />
      </label>
    </div>
  );
}

export function FolderFooter({
  visible,
  total,
  onLoadMore,
  pageSize,
  className = "",
}) {
  const remaining = total - visible;
  const end = Math.min(visible, total);
  const summary = end > 0 ? `Showing 1-${end} of ${total}` : `Showing 0 of ${total}`;
  return (
    <div
      className={[
        "flex flex-wrap items-center gap-x-3 gap-y-2 pl-2",
        className,
      ].filter(Boolean).join(" ")}
    >
      <span className="min-w-0 text-[10px] leading-[1.24] text-[color:var(--muted)]">
        {summary}
      </span>
      {visible < total ? (
        <button
          type="button"
          className="ml-auto inline-flex min-h-[24px] shrink-0 items-center justify-center border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[10px] leading-none font-medium whitespace-nowrap text-[color:var(--ink)] transition-colors hover:border-[color:var(--line-strong)] hover:bg-[color:var(--control-raised-bg)]"
          onClick={onLoadMore}
        >
          {`Load ${Math.min(pageSize, remaining)} more`}
        </button>
      ) : null}
    </div>
  );
}
